import { useState, type FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Mail, MessageCircle, Send, Check, AlertCircle, Clock, ChevronDown, HelpCircle } from 'lucide-react';
import { sendContactEmail } from '@/lib/emailjs';
import { Button } from '@/components/ui/button';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

interface FormErrors {
    name?: string;
    email?: string;
    subject?: string;
    message?: string;
}

export function ContactSection() {
    const { t } = useTranslation();
    const [status, setStatus] = useState<FormStatus>('idle');
    const [errors, setErrors] = useState<FormErrors>({});
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
    });

    // Track focus for floating labels
    const [focused, setFocused] = useState<Record<string, boolean>>({});

    const subjects = [
        { value: 'consumer', label: t('contact.form.subjects.consumer') },
        { value: 'business', label: t('contact.form.subjects.business') },
        { value: 'partnership', label: t('contact.form.subjects.partnership') },
        { value: 'other', label: t('contact.form.subjects.other') },
    ];

    function validate(): boolean {
        const newErrors: FormErrors = {};
        if (!formData.name.trim()) newErrors.name = t('contact.form.validation.nameRequired');
        if (!formData.email.trim()) {
            newErrors.email = t('contact.form.validation.emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('contact.form.validation.emailInvalid');
        }
        if (!formData.subject) newErrors.subject = t('contact.form.validation.subjectRequired');
        if (!formData.message.trim()) newErrors.message = t('contact.form.validation.messageRequired');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        if (!validate()) return;

        setStatus('sending');
        try {
            await sendContactEmail({
                name: formData.name,
                email: formData.email,
                subject: subjects.find(s => s.value === formData.subject)?.label || formData.subject,
                message: formData.message,
            });
            setStatus('success');
            setFormData({ name: '', email: '', subject: '', message: '' });
            setTimeout(() => setStatus('idle'), 5000);
        } catch {
            setStatus('error');
            setTimeout(() => setStatus('idle'), 4000);
        }
    }

    function handleChange(field: string, value: string) {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field as keyof FormErrors]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    }

    const isFloating = (field: string) => focused[field] || formData[field as keyof typeof formData];

    const stagger = {
        hidden: {},
        visible: {
            transition: { staggerChildren: 0.1, delayChildren: 0.1 }
        }
    };

    const fadeUp = {
        hidden: { opacity: 0, y: 24 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.04, 0.62, 0.23, 0.98] } }
    };

    return (
        <section id="contact-section" className="py-24 bg-[#FDF8F3] relative overflow-hidden">
            <div className="container mx-auto px-4 max-w-6xl relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-16 items-start">

                    {/* ─── Left: Info ─── */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                        variants={stagger}
                        className="space-y-10 lg:sticky lg:top-24"
                    >
                        {/* Header */}
                        <motion.div variants={fadeUp} className="text-left">
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#6F4E37]/10 text-[#6F4E37] text-sm font-medium mb-6"
                            >
                                <HelpCircle size={14} />
                                <span className="uppercase tracking-wider text-xs font-bold">{t('contact.badge')}</span>
                            </motion.div>
                            <h2 className="text-4xl md:text-5xl font-display font-bold text-[#2D1810] mb-4">
                                {t('contact.title')}
                            </h2>
                            <p className="text-lg text-[#6B5344] leading-relaxed">
                                {t('contact.subtitle')}
                            </p>
                        </motion.div>

                        {/* Contact Cards */}
                        <motion.div variants={fadeUp} className="space-y-4">
                            {/* Email Card */}
                            <a
                                href={`mailto:${t('contact.info.email')}`}
                                className="group flex items-center gap-4 p-5 rounded-2xl bg-white/60 backdrop-blur-sm border border-[#E5DCD3] hover:border-[#6F4E37]/30 hover:bg-white/80 transition-all duration-300"
                            >
                                <div className="w-12 h-12 rounded-xl bg-[#6F4E37]/10 flex items-center justify-center group-hover:bg-[#6F4E37]/15 transition-colors">
                                    <Mail className="w-5 h-5 text-[#6F4E37]" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-[#9C8979]">Email</p>
                                    <p className="text-[#2D1810] font-medium group-hover:text-[#6F4E37] transition-colors">
                                        {t('contact.info.email')}
                                    </p>
                                </div>
                            </a>

                            {/* WhatsApp Card */}
                            <a
                                href={t('contact.info.whatsappLink')}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-center gap-4 p-5 rounded-2xl bg-white/60 backdrop-blur-sm border border-[#E5DCD3] hover:border-[#6F4E37]/30 hover:bg-white/80 transition-all duration-300"
                            >
                                <div className="w-12 h-12 rounded-xl bg-[#6F4E37]/10 flex items-center justify-center group-hover:bg-[#6F4E37]/15 transition-colors">
                                    <MessageCircle className="w-5 h-5 text-[#6F4E37]" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-[#9C8979]">WhatsApp</p>
                                    <p className="text-[#2D1810] font-medium group-hover:text-[#6F4E37] transition-colors">
                                        {t('contact.info.whatsapp')}
                                    </p>
                                </div>
                            </a>
                        </motion.div>

                        <hr className="border-[#E5DCD3]/50" />

                        {/* Response Time (Desktop Only) */}
                        <motion.div variants={fadeUp} className="hidden lg:flex items-center gap-3 text-[#6B5344]">
                            <Clock className="w-4 h-4 text-[#9C8979]" />
                            <p className="text-sm">{t('contact.info.responseTime')}</p>
                        </motion.div>
                    </motion.div>

                    {/* ─── Right: Form ─── */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                        variants={fadeUp}
                    >
                        <div className="relative p-8 md:p-10 rounded-3xl bg-white/70 backdrop-blur-xl border border-[#E5DCD3]/80 shadow-[0_8px_32px_rgba(45,24,16,0.08)]">
                            <form onSubmit={handleSubmit} className="space-y-6" noValidate>

                                {/* Name */}
                                <div className="relative">
                                    <input
                                        id="contact-name"
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        onFocus={() => setFocused(f => ({ ...f, name: true }))}
                                        onBlur={() => setFocused(f => ({ ...f, name: false }))}
                                        className={`peer w-full px-4 pt-6 pb-2 rounded-xl border bg-transparent text-[#2D1810] text-base outline-none transition-all duration-300 placeholder-transparent ${errors.name ? 'border-red-400 focus:ring-2 focus:ring-red-400/20' : 'border-[#E5DCD3] focus:border-[#6F4E37] focus:ring-2 focus:ring-[#6F4E37]/10'
                                            }`}
                                        placeholder={t('contact.form.namePlaceholder')}
                                    />
                                    <label
                                        htmlFor="contact-name"
                                        className={`absolute left-4 transition-all duration-200 pointer-events-none ${isFloating('name')
                                            ? 'top-2 text-xs font-medium text-[#6F4E37]'
                                            : 'top-4 text-base text-[#9C8979]'
                                            }`}
                                    >
                                        {t('contact.form.name')}
                                    </label>
                                    {errors.name && (
                                        <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" /> {errors.name}
                                        </p>
                                    )}
                                </div>

                                {/* Email */}
                                <div className="relative">
                                    <input
                                        id="contact-email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        onFocus={() => setFocused(f => ({ ...f, email: true }))}
                                        onBlur={() => setFocused(f => ({ ...f, email: false }))}
                                        className={`peer w-full px-4 pt-6 pb-2 rounded-xl border bg-transparent text-[#2D1810] text-base outline-none transition-all duration-300 placeholder-transparent ${errors.email ? 'border-red-400 focus:ring-2 focus:ring-red-400/20' : 'border-[#E5DCD3] focus:border-[#6F4E37] focus:ring-2 focus:ring-[#6F4E37]/10'
                                            }`}
                                        placeholder={t('contact.form.emailPlaceholder')}
                                    />
                                    <label
                                        htmlFor="contact-email"
                                        className={`absolute left-4 transition-all duration-200 pointer-events-none ${isFloating('email')
                                            ? 'top-2 text-xs font-medium text-[#6F4E37]'
                                            : 'top-4 text-base text-[#9C8979]'
                                            }`}
                                    >
                                        {t('contact.form.email')}
                                    </label>
                                    {errors.email && (
                                        <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" /> {errors.email}
                                        </p>
                                    )}
                                </div>

                                {/* Subject */}
                                <div className="relative">
                                    <label
                                        htmlFor="contact-subject"
                                        className="absolute left-4 top-2 text-xs font-medium text-[#6F4E37] transition-all duration-200 pointer-events-none z-10"
                                    >
                                        {t('contact.form.subject')}
                                    </label>
                                    <select
                                        id="contact-subject"
                                        value={formData.subject}
                                        onChange={(e) => handleChange('subject', e.target.value)}
                                        className={`peer w-full px-4 pt-6 pb-2 rounded-xl border bg-transparent text-base outline-none appearance-none cursor-pointer transition-all duration-300 ${!formData.subject ? 'text-[#9C8979]' : 'text-[#2D1810]'
                                            } ${errors.subject ? 'border-red-400 focus:ring-2 focus:ring-red-400/20' : 'border-[#E5DCD3] focus:border-[#6F4E37] focus:ring-2 focus:ring-[#6F4E37]/10'
                                            }`}
                                    >
                                        <option value="" disabled>{t('contact.form.subjectPlaceholder')}</option>
                                        {subjects.map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9C8979] pointer-events-none" />
                                    {errors.subject && (
                                        <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" /> {errors.subject}
                                        </p>
                                    )}
                                </div>

                                {/* Message */}
                                <div className="relative">
                                    <textarea
                                        id="contact-message"
                                        value={formData.message}
                                        onChange={(e) => handleChange('message', e.target.value)}
                                        onFocus={() => setFocused(f => ({ ...f, message: true }))}
                                        onBlur={() => setFocused(f => ({ ...f, message: false }))}
                                        rows={4}
                                        className={`peer w-full px-4 pt-6 pb-2 rounded-xl border bg-transparent text-[#2D1810] text-base outline-none resize-none transition-all duration-300 placeholder-transparent ${errors.message ? 'border-red-400 focus:ring-2 focus:ring-red-400/20' : 'border-[#E5DCD3] focus:border-[#6F4E37] focus:ring-2 focus:ring-[#6F4E37]/10'
                                            }`}
                                        placeholder={t('contact.form.messagePlaceholder')}
                                    />
                                    <label
                                        htmlFor="contact-message"
                                        className={`absolute left-4 transition-all duration-200 pointer-events-none ${isFloating('message')
                                            ? 'top-2 text-xs font-medium text-[#6F4E37]'
                                            : 'top-4 text-base text-[#9C8979]'
                                            }`}
                                    >
                                        {t('contact.form.message')}
                                    </label>
                                    {errors.message && (
                                        <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" /> {errors.message}
                                        </p>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    size="lg"
                                    disabled={status === 'sending'}
                                    className="w-full text-base font-semibold transition-all rounded-xl duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 h-14 bg-primary text-primary-foreground hover:bg-primary/90"
                                >
                                    {status === 'sending' ? (
                                        <>
                                            <motion.div
                                                animate={{ rotate: 360 }}
                                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                                className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full mr-2"
                                            />
                                            {t('contact.form.sending')}
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4 mr-2" />
                                            {t('contact.form.submit')}
                                        </>
                                    )}
                                </Button>

                                {/* Response Time (Mobile Only) */}
                                <div className="lg:hidden flex items-center gap-3 text-[#6B5344] mt-6">
                                    <Clock className="w-4 h-4 text-[#9C8979]" />
                                    <p className="text-sm">{t('contact.info.responseTime')}</p>
                                </div>

                                {/* Success / Error Feedback */}
                                <AnimatePresence>
                                    {status === 'success' && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            className="flex items-start gap-3 p-4 rounded-xl bg-[#4CAF50]/10 border border-[#4CAF50]/20"
                                        >
                                            <Check className="w-5 h-5 text-[#4CAF50] mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-[#2D1810]">{t('contact.form.success')}</p>
                                                <p className="text-xs text-[#6B5344] mt-1">{t('contact.form.successDetail')}</p>
                                            </div>
                                        </motion.div>
                                    )}
                                    {status === 'error' && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-200"
                                        >
                                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                                            <p className="text-sm text-red-600">{t('contact.form.error')}</p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </form>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Background Decorations */}
            <div className="absolute top-1/3 left-0 w-72 h-72 bg-[#6F4E37]/5 rounded-full blur-3xl pointer-events-none -translate-x-1/2" />
            <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#D4A574]/8 rounded-full blur-3xl pointer-events-none translate-x-1/3" />
        </section>
    );
}
